import * as Sentry from "@sentry/nextjs";
import { DebugBlocks, resolveEnabledDebugBlocks } from "./debug";

interface SentryLoggerProps {
  error?: unknown;
  type?: "error" | "warning" | "debug";
  message?: string;
  context?: Record<string, any>;
  tags?: {
    source?: "api" | "server" | "auth" | "middleware" | string;
    endpoint?: string;
  } & Record<string, string>;
  throwError?: boolean;
  debugBlock?: DebugBlocks;
}

/**
 * Logs specifically defined error, warning and debug cases to sentry.
 * If `DEBUG` env is enabled these cases will also be logged in console with trace.
 *
 * @param error - Must be a valid `Error` object for stack trace to be captured.
 * @param type - Defaults to `error`.
 * Sentry message will be captured instead of error in case of `debug` or `warning`.
 * @param message - String message with case specification.
 * @param context - Object with additional data to be captured.
 * @param tags - Sentry tags used for error specification, like location or env, for easier filtration and debug.
 * @param throwError - By default all errors passed to sentryLogger aren't propagated further.
 * If error should be thrown set `throwError` to `true`.
 * @param debugBlock - Log only specific parts of the app.
 * This can be set as comma separated string of debug blocks via `NEXT_PUBLIC_ENABLED_DEBUG_BLOCKS`.
 */
export const sentryLogger = ({ error, type = "error", message, debugBlock, context, tags, throwError }: SentryLoggerProps) => {
  try {
    const shouldLogDebug = process.env.NEXT_PUBLIC_DEBUG === "true" && (debugBlock ? resolveEnabledDebugBlocks(debugBlock) : true);

    shouldLogDebug &&
      console.debug({
        type,
        ...(debugBlock && { debugBlock }),
        ...(error !== "undefined" && typeof error === "object" && { error }),
        ...(message && { message }),
        ...(context && { context }),
        ...(tags && { tags }),
      });

    if (type !== "error" && message) {
      if (type === "debug" && !shouldLogDebug) {
        return;
      }

      // Does not capture stack trace
      Sentry.captureMessage(message, {
        level: type,
        ...(tags && { tags }),
        ...(context && {
          extra: { context },
        }),
      });
    } else if (error) {
      Sentry.captureException(error, {
        ...(tags && { tags }),
        ...((message || context) && {
          extra: {
            ...(message && { message }),
            ...(context && { context }),
          },
        }),
      });
    }
  } catch (e) {
    // Silent catch by default: don't let logging fail anything

    if (throwError) {
      throw e;
    }
  }
};
